/**
 * Spaced Repetition System (SRS)
 *
 * Simplified SRS algorithm inspired by Anki, but gentler and more forgiving.
 * Helps users review quizzes at optimal intervals for long-term retention.
 *
 * Philosophy:
 * - Excellent performance (≥95%): Advance to next interval
 * - Good performance (70-95%): Keep current interval
 * - Poor performance (<70%): Reset to earlier interval
 * - Add ±20% randomization to prevent predictable "always due on Monday" patterns
 */

/**
 * Standard SRS intervals in days
 * 1d → 3d → 7d → 14d → 30d → 60d → 120d → 180d
 */
const SRS_INTERVALS = [1, 3, 7, 14, 30, 60, 120, 180] as const;

/**
 * Calculate the next review date based on performance
 *
 * @param lastReviewDate - Date of last quiz completion
 * @param performanceScore - Completion rate (0-1)
 * @param reviewCount - Number of times this quiz has been reviewed
 * @returns Next review date and new interval
 */
export function calculateNextReview(
  lastReviewDate: Date,
  performanceScore: number,
  reviewCount: number
): { nextReviewDate: Date; intervalDays: number } {
  // Get current interval index (capped at max)
  const intervalIndex = Math.min(reviewCount, SRS_INTERVALS.length - 1);
  let interval = SRS_INTERVALS[intervalIndex] ?? 1; // Fallback to 1 day if undefined

  // Adjust based on performance
  if (performanceScore >= 0.95) {
    // Excellent: Move to next interval
    const nextIndex = Math.min(intervalIndex + 1, SRS_INTERVALS.length - 1);
    interval = SRS_INTERVALS[nextIndex] ?? interval;
  } else if (performanceScore < 0.7) {
    // Poor: Reset to earlier interval
    const prevIndex = Math.max(0, intervalIndex - 1);
    interval = SRS_INTERVALS[prevIndex] ?? interval;
  }
  // else: Keep current interval (70-95% performance)

  // Add ±20% randomization (prevents staleness, feels more organic)
  const randomFactor = 0.8 + Math.random() * 0.4;
  const randomizedInterval = Math.floor(interval * randomFactor);

  // Calculate next review date
  const nextReviewDate = new Date(lastReviewDate);
  nextReviewDate.setDate(nextReviewDate.getDate() + randomizedInterval);

  return {
    nextReviewDate,
    intervalDays: randomizedInterval,
  };
}

/**
 * Check if a quiz is due for review
 *
 * @param nextReviewDate - Scheduled review date
 * @param now - Current date (optional, defaults to now)
 * @returns true if due for review
 */
export function isDueForReview(nextReviewDate: Date, now: Date = new Date()): boolean {
  return now >= nextReviewDate;
}

/**
 * Get days until next review (negative if overdue)
 *
 * @param nextReviewDate - Scheduled review date
 * @param now - Current date (optional, defaults to now)
 * @returns Number of days (negative if overdue)
 */
export function getDaysUntilReview(
  nextReviewDate: Date,
  now: Date = new Date()
): number {
  const diffMs = nextReviewDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return diffDays;
}

/**
 * Get days since last review
 *
 * @param lastReviewDate - Date of last review
 * @param now - Current date (optional, defaults to now)
 * @returns Number of days since last review
 */
export function getDaysSinceReview(
  lastReviewDate: Date,
  now: Date = new Date()
): number {
  const diffMs = now.getTime() - lastReviewDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return diffDays;
}

/**
 * Format days until review as human-readable string
 *
 * @param days - Number of days (negative if overdue)
 * @returns Formatted string (e.g., "Due today", "Overdue by 3 days", "Due in 5 days")
 */
export function formatDaysUntilReview(days: number): string {
  if (days < 0) {
    const overdueDays = Math.abs(days);
    if (overdueDays === 1) return "Overdue by 1 day";
    return `Overdue by ${overdueDays} days`;
  } else if (days === 0) {
    return "Due today";
  } else if (days === 1) {
    return "Due tomorrow";
  } else if (days <= 7) {
    return `Due in ${days} days`;
  } else if (days <= 30) {
    const weeks = Math.floor(days / 7);
    return `Due in ${weeks} ${weeks === 1 ? "week" : "weeks"}`;
  } else {
    const months = Math.floor(days / 30);
    return `Due in ${months} ${months === 1 ? "month" : "months"}`;
  }
}

/**
 * Initialize SRS fields for a new knowledge state
 *
 * @param completionRate - Initial completion rate (0-1)
 * @returns Initial SRS values
 */
export function initializeSRS(completionRate: number): {
  lastReviewDate: string;
  nextReviewDate: string;
  reviewCount: number;
  intervalDays: number;
} {
  const now = new Date();
  const { nextReviewDate, intervalDays } = calculateNextReview(now, completionRate, 0);

  return {
    lastReviewDate: now.toISOString(),
    nextReviewDate: nextReviewDate.toISOString(),
    reviewCount: 1,
    intervalDays,
  };
}

/**
 * Update SRS fields after completing a quiz
 *
 * @param existingSRS - Current SRS fields
 * @param newCompletionRate - New completion rate (0-1)
 * @returns Updated SRS values
 */
export function updateSRS(
  existingSRS: {
    reviewCount: number;
    lastReviewDate: string;
  },
  newCompletionRate: number
): {
  lastReviewDate: string;
  nextReviewDate: string;
  reviewCount: number;
  intervalDays: number;
} {
  const now = new Date();
  const { nextReviewDate, intervalDays } = calculateNextReview(
    now,
    newCompletionRate,
    existingSRS.reviewCount
  );

  return {
    lastReviewDate: now.toISOString(),
    nextReviewDate: nextReviewDate.toISOString(),
    reviewCount: existingSRS.reviewCount + 1,
    intervalDays,
  };
}
